import { Component, HostListener, Inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { PlayerService } from './services/player.service';
import { AppService } from './services/app.service';
import { AuthService } from './services/auth.service';
import { DOCUMENT } from '@angular/common';
import { EventEmitter } from 'events';
import { BackgroundMode } from '@ionic-native/background-mode/ngx';
import { MobileAccessibility } from '@ionic-native/mobile-accessibility/ngx';
import { ScreenOrientation } from '@ionic-native/screen-orientation/ngx';
import { User } from './model/user.model';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SchedulerAccessService } from './services/scheduler-access.service';
import { SchedulerNavRevealService } from './services/scheduler-nav-reveal.service';

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss']
})
export class AppComponent {
	public nyActive: boolean = false;
	public isEmbeddedSchedulerRoute: boolean = false;
	/** On /scheduler, mobile landscape: hide header + tab bar for more grid space. */
	public schedulerImmersiveChrome = false;
	/** On /scheduler, mobile portrait: suggest rotating phone for better weekly grid. */
	public showSchedulerRotateHint = false;
	/** Immersive scheduler hides the tab bar; the page can ask for it back (title tap / scrolled to bottom). */
	public schedulerNavRevealed = false;
	public rotateHintButtons = [{ text: 'Понятно', role: 'cancel' as const }];
	private readonly schedulerRotateHintDismissedKey = 'schedulerRotateHintDismissed';
	/** Whether the hidden Scheduler entry is unlocked on this device (secret 7-tap on the logo). */
	public schedulerUnlocked = false;

	constructor(
		private platform: Platform,
		private splashScreen: SplashScreen,
		private statusBar: StatusBar,
		private playerService: PlayerService,
		private appService: AppService,
		private authService: AuthService,
		private backgroundMode: BackgroundMode,
		private mobileAccessibility: MobileAccessibility,
		private router: Router,
		private schedulerAccess: SchedulerAccessService,
		private schedulerNavReveal: SchedulerNavRevealService,
		private screenOrientation: ScreenOrientation,
		@Inject(DOCUMENT) document,
	) {
		this.initializeApp();
		this.trackEmbeddedRoute();
		this.updateSchedulerImmersiveChrome();
		this.updateSchedulerRotateHint();
		this.schedulerUnlocked = this.schedulerAccess.isUnlocked();
		this.schedulerNavReveal.revealed$.subscribe(revealed => {
			this.schedulerNavRevealed = revealed;
		});
	}

	/**
	 * Secret access to the new Scheduler: tap/click the logo 7 times within 4 seconds to reveal
	 * the Scheduler tab on this device. Lets us QA in PROD under the client's account without
	 * regular customers seeing it. Works the same with mouse (desktop) and touch (mobile).
	 */
	public onLogoTap(): void {
		if (this.schedulerAccess.registerLogoTap()) {
			this.schedulerUnlocked = true;
			// Open the scheduler right away: unmistakable confirmation and immediate access,
			// independent of whether the (dynamically added) tab button has re-rendered yet.
			this.router.navigateByUrl('/scheduler');
		} else {
			// Preserve the logo's normal "home" behavior for ordinary taps.
			this.router.navigateByUrl('/tab1');
		}
	}

	get loading(): string { return this.appService.loading; }
	get notification(): string { return this.appService.notification; }
	get notificationClass(): string { return this.appService.notificationClass; }
	get authenticated(): boolean { return this.authService.authenticated; }
	get currentUser(): User { return this.authService.currentUser; }
	get countOfBoldPlaylists(): number { return this.playerService.state.playlists.filter(x => x.bold).length; }
	get showAppChrome(): boolean { return !this.isEmbeddedSchedulerRoute; }
	get isCordova(): boolean { return this.platform.is('cordova'); }

	private initializeApp(): void {
		this.platform.ready().then(() => {
			try {
				const isCordova = this.platform.is('cordova');
				if (isCordova) {
					this.statusBar.styleDefault();
					this.splashScreen.hide();

					// Keep the whole app portrait by default (iOS allows landscape at the OS level so
					// the scheduler can rotate; Android is portrait via the manifest). The scheduler
					// screen unlocks orientation on entry and re-locks portrait on leave.
					try {
						this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT);
					} catch (e) {
						// ignore: orientation locking is best-effort
					}

					// Enable background mode only on real device/simulator with Cordova.
					this.backgroundMode.enable();
					this.backgroundMode.setDefaults({
						title: 'YCD App',
						text: 'Running in background',
						silent: true
					});
				}

				var platformWidth: number = this.platform.width();
				if (isCordova && platformWidth <= 400) {
					this.mobileAccessibility.usePreferredTextZoom(false);
					this.mobileAccessibility.setTextZoom(100);
				}

				this.platform.pause.subscribe(() => {
					console.log('App paused - going to background');
					try {
						this.playerService.platformPauseSubject.next(true);
					} catch (error) {
						console.error('Error during pause:', error);
					}
				});

				this.platform.resume.subscribe(() => {
					console.log('App resumed - coming to foreground');
					try {
						this.playerService.platformPauseSubject.next(false);
					} catch (error) {
						console.error('Error during resume:', error);
					}
				});
			} catch (error) {
				console.error('Error during app initialization:', error);
			}
		}).catch(error => {
			console.error('Platform ready error:', error);
		});
	}

	private trackEmbeddedRoute(): void {
		const updateFlag = (url: string): void => {
			const cleanUrl = ((url || '').split('?')[0] || '').replace(/\/+$/, '');
			this.isEmbeddedSchedulerRoute = cleanUrl === '/scheduler/embedded';
		};

		updateFlag(this.router.url);
		this.updateSchedulerImmersiveChrome();
		this.updateSchedulerRotateHint();

		this.router.events
			.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe((event) => {
				const navEvent = event as NavigationEnd;
				updateFlag(navEvent.urlAfterRedirects || navEvent.url || '');
				this.updateSchedulerImmersiveChrome();
				this.updateSchedulerRotateHint();
			});
	}

	private updateSchedulerImmersiveChrome(): void {
		try {
			if (typeof window === 'undefined') {
				this.schedulerImmersiveChrome = false;
				return;
			}
			const cleanUrl = ((this.router.url || '').split('?')[0] || '').replace(/\/+$/, '');
			const isSchedulerRoute = cleanUrl === '/scheduler' || cleanUrl.startsWith('/scheduler/');
			const w = window.innerWidth || 0;
			// Width-only (w<=767) misses real phone landscape (width can be 844). Use the platform too.
			const isMobilePlatform =
				!!this.platform &&
				(
					this.platform.is('ios') ||
					this.platform.is('android') ||
					this.platform.is('mobile') ||
					this.platform.is('mobileweb') ||
					this.platform.is('hybrid')
				);
			const mobile = isMobilePlatform || w <= 767;
			const landscape =
				typeof window.matchMedia === 'function'
					? window.matchMedia('(orientation: landscape)').matches
					: window.innerHeight < window.innerWidth;
			this.schedulerImmersiveChrome = isSchedulerRoute && mobile && landscape;
		} catch {
			this.schedulerImmersiveChrome = false;
		}
	}

	private updateSchedulerRotateHint(): void {
		try {
			if (typeof window === 'undefined') {
				this.showSchedulerRotateHint = false;
				return;
			}

			if (this.isEmbeddedSchedulerRoute) {
				this.showSchedulerRotateHint = false;
				return;
			}

			const dismissed = window.sessionStorage.getItem(this.schedulerRotateHintDismissedKey) === '1';

			if (dismissed) {
				this.showSchedulerRotateHint = false;
				return;
			}

			const cleanUrl = ((this.router.url || '').split('?')[0] || '').replace(/\/+$/, '');
			const isSchedulerRoute = cleanUrl === '/scheduler' || cleanUrl.startsWith('/scheduler/');
			const w = window.innerWidth || 0;
			const mobile = w <= 767;
			const portrait =
				typeof window.matchMedia === 'function'
					? window.matchMedia('(orientation: portrait)').matches
					: window.innerHeight >= window.innerWidth;

			this.showSchedulerRotateHint = isSchedulerRoute && mobile && portrait;
		} catch {
			this.showSchedulerRotateHint = false;
		}
	}

	@HostListener('window:resize')
	@HostListener('window:orientationchange')
	onSchedulerChromeLayout(): void {
		this.updateSchedulerImmersiveChrome();
		this.updateSchedulerRotateHint();
	}

	public onRotateHintDismiss(): void {
		try {
			window.sessionStorage.setItem(this.schedulerRotateHintDismissedKey, '1');
		} catch {
			// ignore
		} finally {
			this.showSchedulerRotateHint = false;
		}
	}

	public ny1(event: MouseEvent): void {
		event.stopPropagation();
		this.playerService.ny1();
		this.nyActive = false;
	}

	public ny2(event: MouseEvent): void {
		event.stopPropagation();

		if (confirm("Are you sure?")) {
			this.playerService.ny2();
			this.nyActive = false;
		}
	}

	public onMainWindowClick(event: MouseEvent): void {
		this.nyActive = false;
	}

	/**
	 * Opens the account/cabinet page. Uses a programmatic navigation instead of a passive routerLink:
	 * on the immersive scheduler screen the routerLink on the header item did not navigate, so we
	 * reset the scheduler nav-reveal state and navigate explicitly.
	 */
	public goToCabinet(): void {
		try {
			this.schedulerNavReveal.reset();
		} catch {
			// ignore
		}
		this.router.navigateByUrl('/auth/cabinet');
	}

	public onNyPanelClick(event: MouseEvent): void {
		event.stopPropagation();
	}

	public toggleNyPanel(event: MouseEvent): void {
		event.stopPropagation();
		this.nyActive = !this.nyActive;
	}

	public playingNowClick(event: MouseEvent): void {
		if (document.URL.includes("tab1")) {
			let re: HTMLElement = document.getElementsByClassName('playlist-open')[0] as HTMLElement;
			if (re !== null) {
				re.click();
			}
		}
	}

	public playlistsClick(event: EventEmitter): void {
		let re: HTMLElement = document.getElementById('playlistsongs-close') as HTMLElement;
		if (re != null) {
			re.click();
		}
	}
}
